import { type User } from '@prisma/client';
import { differenceInHours, format, formatDistanceStrict } from 'date-fns';

/**
 * * https://date-fns.org/v3.6.0/docs/format has formatting options
 * @param date
 * @param dateFormat
 * @returns
 */
export function formatDate(date: Date | null | string | undefined, dateFormat = 'MM/dd/yyyy'): string | null {
  if (!date) {
    return null;
  }

  return format(new Date(date), dateFormat);
}

/**
 * * Note: This only works for dates in ISO-2801 format that have no offset and the time is 00:00:00
 * * Additionally, only accepts `date` formats, not `datetime` formats
 * * It's a workaround so that we don't need to install date-fns-tz
 *
 * @param date
 * @param dateFormat
 * @returns
 */
export function formatDateUTC(date: Date | null | string | undefined, dateFormat = 'MM/dd/yyyy'): string | null {
  if (!date) {
    return null;
  }

  return formatDate(new Date(date).toISOString().replace('T00:00:00.000Z', 'T23:59:59.999Z'), dateFormat);
}

export function formatDisplayName(
  record?: null | undefined | { firstName: string | null; lastName: string | null; middleName?: string | null } | User
) {
  if (!record) {
    return '';
  }

  if (record.firstName) {
    const middleName = 'middleName' in record ? formatMiddleName(record.middleName) : '';
    const middleNamePart = middleName ? ` ${middleName}` : '';
    const lastName = record.lastName ? ` ${record.lastName}` : '';
    return `${record.firstName}${middleNamePart}${lastName}`;
  }

  return '';
}

export function formatAddress(
  record:
    | null
    | undefined
    | {
        streetAddress1: string | null;
        streetAddress2?: string | null;
        city: string | null;
        state: string | null;
        postalCode: string | null;
      }
): null | string {
  if (!record) {
    return null;
  }

  const parts: string[] = [];

  if (record?.streetAddress1) {
    parts.push(record.streetAddress1);
  }

  if (record.streetAddress2) {
    parts.push(record.streetAddress2);
  }

  if (record.city) {
    parts.push(record.city);
  }

  if (record.state) {
    parts.push(record.postalCode ? `${record.state} ${record.postalCode}` : record.state);
  } else if (record.postalCode) {
    parts.push(record.postalCode);
  }

  return parts.join(', ');
}

/**
 * Checks if a middle name is NMI, N/A, or a dash (-) and returns an empty string if true
 * @param middleName - The middle name to check
 * @returns Empty string if middle name is NMI, N/A, or dash (-), otherwise returns the original middle name
 */
export function formatMiddleName(middleName: string | null | undefined): string {
  if (!middleName) {
    return '';
  }

  const trimmedMiddleName = middleName.trim().toUpperCase();

  // Check for common placeholder values
  if (trimmedMiddleName === 'NMI' || trimmedMiddleName === 'N/A' || trimmedMiddleName === '-') {
    return '';
  }

  return middleName;
}

/**
 * * Returns an empty string if the value is null, undefined, or an empty string
 * @param value
 * @returns
 */
export function formatEmptyString(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return value;
}

/**
 * Formats the date range text dynamically based on whether it's recent or historical
 * - If endDate is within 24 hours of now: "for the last X days/months/years"
 * - If endDate is historical: "from [date] to [date]"
 *
 * @param startDate - The start date of the range
 * @param endDate - The end date of the range
 * @returns Formatted date range text
 *
 * @example
 * // If today is Nov 5, 2025
 * formatDateRangeText(new Date('2025-10-05'), new Date('2025-11-05'))
 * // Returns: "for the last 1 month"
 *
 * @example
 * formatDateRangeText(new Date('2024-01-01'), new Date('2024-01-31'))
 * // Returns: "from Jan 1, 2024 to Jan 31, 2024"
 */
export function formatDateRangeText(startDate: Date, endDate: Date): string {
  const now = new Date();
  const hoursFromNow = Math.abs(differenceInHours(endDate, now));

  // If end date is within 24 hours of now, treat it as "recent" data
  if (hoursFromNow <= 24) {
    const duration = formatDistanceStrict(startDate, endDate);
    return `for the last ${duration}`;
  }

  // Otherwise, show specific date range for historical data
  const formattedStart = format(startDate, 'MMM d, yyyy');
  const formattedEnd = format(endDate, 'MMM d, yyyy');
  return `from ${formattedStart} to ${formattedEnd}`;
}

/**
 * Converts a date value (string, Date, or null) to a Date object or null.
 * Useful for converting dates that come from JSON-serialized Prisma responses.
 * Preserves the nullability of the input type.
 *
 * @param date - The date value to convert (string, Date, or null)
 * @returns Date object if value exists, null otherwise
 *
 * @example
 * toDate('2024-01-01T00:00:00.000Z') // Returns Date
 * toDate(null) // Returns null
 * toDate(undefined) // Returns null
 *
 * // Type preservation:
 * const date: string | null = '2024-01-01';
 * const result = toDate(date); // Type: Date | null
 *
 * const requiredDate: string = '2024-01-01';
 * const result2 = toDate(requiredDate); // Type: Date
 */
export function toDate(date: string | Date): Date;
export function toDate(date: string | Date | null): Date | null;
export function toDate(date: string | Date | undefined): Date | null;
export function toDate(date: string | Date | null | undefined): Date | null;
export function toDate(date: string | Date | null | undefined): Date | null {
  if (!date) {
    return null;
  }
  return date instanceof Date ? date : new Date(date);
}
